/**
 * coinDropEmitter — broadcast simples para a UI saber quando uma operação de
 * crédito de Livra Coins foi efetivamente aplicada (Sprint G §4.1).
 *
 * Modelo "EventTarget" nativo (sem libs). Quem dispara é o `economyService`,
 * quem escuta é o `<CoinDropProvider />` global montado em AppRoutes.
 *
 * Uma payload de coin-drop carrega:
 *  - amount        — quanto foi creditado nesta operação.
 *  - balanceAfter  — saldo final após a aplicação.
 *  - reasonCode    — motivo (POMODORO_SESSION, BOOK_COMPLETED…) p/ mensagem.
 *  - originId      — id arbitrário do botão/elemento de origem; opcional.
 *                    Quando informado, o provider tenta animar uma moeda
 *                    voando do elemento `data-coin-origin="<originId>"` até
 *                    o `data-coin-target` (CoinBalance no header).
 */
import type { TransactionReasonCode } from "./economyService";

export interface CoinDropEvent {
  amount: number;
  balanceAfter: number;
  reasonCode: TransactionReasonCode | string;
  originId?: string;
}

const TARGET = new EventTarget();
const EVT = "lura:coin-drop";

export function emitCoinDrop(payload: CoinDropEvent): void {
  TARGET.dispatchEvent(new CustomEvent<CoinDropEvent>(EVT, { detail: payload }));
}

export function subscribeCoinDrop(
  handler: (payload: CoinDropEvent) => void,
): () => void {
  const listener = (evt: Event) => {
    const detail = (evt as CustomEvent<CoinDropEvent>).detail;
    if (detail) handler(detail);
  };
  TARGET.addEventListener(EVT, listener);
  return () => TARGET.removeEventListener(EVT, listener);
}

/**
 * Helper opcional para futuras animações: ler o id de origem mais próximo do
 * botão clicado. Ex.: `<button data-coin-origin="share-card">…</button>`.
 */
export function getCoinOriginId(target: EventTarget | null): string | undefined {
  if (!(target instanceof Element)) return undefined;
  const el = target.closest("[data-coin-origin]");
  if (!el) return undefined;
  const v = el.getAttribute("data-coin-origin");
  return v && v.trim().length > 0 ? v : undefined;
}
